import type { FC, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../../utils/cn';
import uncheckedIcon from '../../../assets/svgs/radio-unchecked.svg';
import checkedIcon from '../../../assets/svgs/radio-checked.svg';

export type RadioOption = {
    label: ReactNode;
    value: string | number;
};

type RadioProps = {
    name: string;
    options: RadioOption[];
    value: string | number;
    onChange: (value: string | number) => void;
    className?: string;
    optionClassName?: string;
    disabled?: boolean;
    'aria-labelledby'?: string;
    'aria-label'?: string;
    inputProps?: Omit<
        InputHTMLAttributes<HTMLInputElement>,
        'type' | 'name' | 'value' | 'checked' | 'onChange' | 'id'
    >;
};

const OPTION_LABEL_CLASS =
    'flex items-center gap-2 cursor-pointer text-sm !leading-relaxed font-medium text-custom-gray-900 w-fit';

const Radio: FC<RadioProps> = ({
    name,
    options,
    value,
    onChange,
    className,
    optionClassName,
    disabled = false,
    'aria-labelledby': ariaLabelledBy,
    'aria-label': ariaLabel,
    inputProps,
}) => {
    return (
        <div
            role="radiogroup"
            aria-labelledby={ariaLabelledBy}
            aria-label={ariaLabelledBy ? undefined : ariaLabel}
            className={cn('flex flex-col gap-2.5', className)}
        >
            {options.map((option) => {
                const id = `${name}-${option.value}`;
                const checked = value === option.value;

                return (
                    <label
                        key={id}
                        htmlFor={id}
                        className={cn(
                            OPTION_LABEL_CLASS,
                            optionClassName,
                            disabled && 'cursor-not-allowed opacity-50'
                        )}
                    >
                        <input
                            {...inputProps}
                            id={id}
                            name={name}
                            type="radio"
                            value={option.value}
                            checked={checked}
                            disabled={disabled}
                            onChange={() => onChange(option.value)}
                            className={cn('peer w-0 h-0 opacity-0', inputProps?.className)}
                        />
                        <span
                            className="flex shrink-0 items-center justify-center rounded-sm peer-focus-visible:ring-2 peer-focus-visible:ring-primary"
                            aria-hidden="true"
                        >
                            <img
                                src={checked ? checkedIcon : uncheckedIcon}
                                alt=""
                                width={18}
                                height={18}
                                className="min-w-[1.125rem]"
                            />
                        </span>
                        <span>{option.label}</span>
                    </label>
                );
            })}
        </div>
    );
};

export default Radio;
